/**
 * Money on the client, mirroring `api/app/services/currency.py`.
 *
 * Amounts are always integer minor units in a `number`. Nothing here parses a formatted
 * string back into an amount, and nothing does arithmetic on formatted output — the two
 * directions are separate on purpose.
 *
 * `number` is safe for this: JavaScript integers are exact to 2^53, which is about
 * 90 trillion dollars in cents. A friend group is not going to get there.
 */

const MINOR_UNIT_EXCEPTIONS: Record<string, number> = {
  BIF: 0,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  ISK: 0,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  PYG: 0,
  RWF: 0,
  UGX: 0,
  UYI: 0,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
  BHD: 3,
  IQD: 3,
  JOD: 3,
  KWD: 3,
  LYD: 3,
  OMR: 3,
  TND: 3,
  CLF: 4,
  UYW: 4,
};

export const DEFAULT_MINOR_UNITS = 2;

export function minorUnits(currency: string): number {
  return MINOR_UNIT_EXCEPTIONS[currency.toUpperCase()] ?? DEFAULT_MINOR_UNITS;
}

export function minorUnitFactor(currency: string): number {
  return 10 ** minorUnits(currency);
}

const formatters = new Map<string, Intl.NumberFormat>();

function formatter(currency: string, style: "currency" | "decimal", signed: boolean) {
  const key = `${currency}:${style}:${signed}`;
  let existing = formatters.get(key);
  if (!existing) {
    const digits = minorUnits(currency);
    existing = new Intl.NumberFormat(undefined, {
      style,
      currency: style === "currency" ? currency : undefined,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      signDisplay: signed ? "exceptZero" : "auto",
    });
    formatters.set(key, existing);
  }
  return existing;
}

/** `formatMoney(15914, "USD")` → `"$159.14"`. */
export function formatMoney(cents: number, currency = "USD"): string {
  return formatter(currency, "currency", false).format(cents / minorUnitFactor(currency));
}

/** A balance or delta, always carrying its sign: `"+$79.57"` / `"−$79.57"`. */
export function formatSigned(cents: number, currency = "USD"): string {
  const text = formatter(currency, "currency", true).format(cents / minorUnitFactor(currency));
  // Intl uses a hyphen; the typographic minus lines up better in tabular figures.
  return text.replace("-", "−");
}

/** Bare number, no symbol — for dense table columns where the currency is in the header. */
export function formatAmount(cents: number, currency = "USD"): string {
  return formatter(currency, "decimal", false).format(cents / minorUnitFactor(currency));
}

/** Parse what someone typed into minor units. Returns null when it is not a number. */
export function parseAmount(input: string, currency = "USD"): number | null {
  const cleaned = input.replace(/[\s,]/g, "").replace(/[^\d.\-]/g, "");
  if (!cleaned || cleaned === "-" || cleaned === "." || cleaned === "-.") return null;
  if (!/^-?\d*\.?\d*$/.test(cleaned)) return null;

  const negative = cleaned.startsWith("-");
  const [whole = "0", fraction = ""] = cleaned.replace("-", "").split(".");
  const digits = minorUnits(currency);
  // Round half up on the first dropped digit rather than trusting float maths.
  const padded = (fraction + "0".repeat(digits + 1)).slice(0, digits + 1);
  const kept = padded.slice(0, digits);
  const next = Number(padded[digits] ?? "0");
  let value = Number(whole || "0") * 10 ** digits + Number(kept || "0");
  if (next >= 5) value += 1;
  return negative ? -value : value;
}

/** The value an amount input should show for an existing amount. */
export function toAmountInput(cents: number, currency = "USD"): string {
  const digits = minorUnits(currency);
  const negative = cents < 0;
  const text = String(Math.abs(cents)).padStart(digits + 1, "0");
  const whole = text.slice(0, text.length - digits) || "0";
  const fraction = digits ? `.${text.slice(text.length - digits)}` : "";
  return `${negative ? "-" : ""}${whole}${fraction}`;
}

export type BalanceTone = "owed" | "owing" | "settled";

/** Positive means the collective owes you. */
export function balanceTone(cents: number): BalanceTone {
  if (cents > 0) return "owed";
  if (cents < 0) return "owing";
  return "settled";
}

export function toneClass(cents: number): string {
  const tone = balanceTone(cents);
  if (tone === "owed") return "text-owed";
  if (tone === "owing") return "text-owing";
  return "text-muted";
}
